import { Fragment, memo, type ReactNode, useMemo } from 'react';

import { type Board, type Clip } from '@air/api/types';
import { type ShortIdProviderProps, useShortIdContext } from '@air/provider-short-id';

import { GalleryItemType } from '~/components/Gallery/types';
import { GalleryNestedEmptyStateHeader } from '~/components/v2/components/shared/gallery/GalleryNestedEmptyStateHeader';
import { useCanShowNestedItemsChip } from '~/components/v2/components/shared/gallery/useCanShowNestedItemsChip';
import { usePublicShowcaseActivePageContent } from '~/components/v2/components/showcase/hooks/usePublicShowcaseActivePageContent';
import { usePublicShowcaseBlocksSearchTotal } from '~/components/v2/components/showcase/hooks/usePublicShowcaseBlocksSearchTotal';
import { PublicShowcaseImageBlock } from '~/components/v2/components/showcase/PublicShowcaseImageBlock';
import { PublicShowcaseLinksBlock } from '~/components/v2/components/showcase/PublicShowcaseLinksBlock';
import {
  getNestedChipHostCategory,
  getPublicShowcaseListingSection,
  PublicShowcaseListingBlock,
  type PublicShowcaseListingBlockCategory,
} from '~/components/v2/components/showcase/PublicShowcaseListingBlock';
import { PublicShowcaseNullState } from '~/components/v2/components/showcase/PublicShowcaseNullState';
import { PublicShowcaseTextBlock } from '~/components/v2/components/showcase/PublicShowcaseTextBlock';
import { PublicShowcaseTitleBlock } from '~/components/v2/components/showcase/PublicShowcaseTitleBlock';
import { PublicShowcaseVideoBlock } from '~/components/v2/components/showcase/PublicShowcaseVideoBlock';
import { isListingContentBlock } from '~/components/v2/components/showcase/showcaseContentBlocks';
import { type GallerySelectableSection, useOrderedGallerySelectedItems } from '~/hooks/useGroupedGallerySelectedItems';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';
import { type UseGalleryViewItemsReturn } from '~/swr-hooks/gallery/types';

type PublicShowcaseContentBlock = NonNullable<ShortIdProviderProps['presentationContent']>['blocks'][number];

// A listing block's items, tagged with the kind of item they are, ready to take its place in the
// page's selectable set.
const getListingBlockSelectableSection = (
  data: UseGalleryViewItemsReturn<Board, Clip>['data'],
  category: PublicShowcaseListingBlockCategory,
): GallerySelectableSection<Board, Clip> => {
  if (category === 'boards') {
    return { type: GalleryItemType.board, items: data.boards.items };
  }

  if (category === 'assets') {
    return { type: GalleryItemType.asset, items: data.clips.items };
  }

  return { type: GalleryItemType.file, items: data.files.items };
};

const getPublicShowcaseBlockContent = (
  block: PublicShowcaseContentBlock,
  nestedChipHostCategory: PublicShowcaseListingBlockCategory | undefined,
): ReactNode => {
  if (isListingContentBlock(block)) {
    return (
      <PublicShowcaseListingBlock category={block.type} isNestedChipHost={block.type === nestedChipHostCategory} />
    );
  }

  if (block.type === 'links') {
    return <PublicShowcaseLinksBlock block={block} />;
  }

  if (block.type === 'image') {
    return <PublicShowcaseImageBlock block={block} />;
  }

  if (block.type === 'video') {
    return <PublicShowcaseVideoBlock block={block} />;
  }

  if (block.type === 'text') {
    return <PublicShowcaseTextBlock block={block} />;
  }

  if (block.type === 'title') {
    return <PublicShowcaseTitleBlock />;
  }

  return null;
};

export const PublicShowcaseBlocks = memo(() => {
  const { presentationSettings, shortId } = useShortIdContext();
  // Root page → the link's presentationContent; sub-page → that page's own content; a root page
  // that was never authored → the implicit default listing blocks (see the hook).
  const activePageContent = usePublicShowcaseActivePageContent();
  // The surface decides how a block presents: the public implementation drops hidden blocks
  // entirely, the editor's wraps every block (hidden included) in its edit shell.
  const { renderBlock, rendersEmptyBlocks, useGalleryViewItems } = usePublicShowcaseContext();

  usePublicShowcaseBlocksSearchTotal({ shortId });

  // The same query each listing block reads — react-query dedupes on the shared key, so the host
  // subscribing here adds no fetch. It is read for the two things only the host can decide: which
  // items are selectable across the whole stack, and whether the stack draws anything at all.
  const itemsData = useGalleryViewItems({ shortId });
  const { canShowNestedItemsChip } = useCanShowNestedItemsChip({
    isDisabled: presentationSettings?.lockedControls?.expandNested === true,
  });
  const { blocks } = activePageContent;
  // The host is the first visible listing that still has items. A hidden or omitted assets block
  // must not keep the chip off the files header, and a page with no such listing uses the empty chip.
  const nestedChipHostCategory = getNestedChipHostCategory(blocks, itemsData.data);
  const showFlattenedChip = canShowNestedItemsChip && !itemsData.isLoading && !nestedChipHostCategory;

  // The page's selectable set, built by walking the blocks IN ORDER: a SHIFT-click range is
  // resolved against this list's order, and the blocks are the author's — a stack that puts Files
  // above Boards must range through files first. Hidden blocks are skipped: their cards are not on
  // screen for a viewer, so making them shift-selectable would select invisible items.
  const selectableSections = useMemo(
    () =>
      blocks
        .filter((block) => !block.hidden)
        .filter(isListingContentBlock)
        .map((block) => getListingBlockSelectableSection(itemsData.data, block.type)),
    [blocks, itemsData.data],
  );

  // ONE registration for the WHOLE stack. The selectable-items store holds a single list and the
  // dispatch replaces it, so the per-gallery registration is switched off inside each listing block
  // (see its `useSelectableItems` prop) and done once here — otherwise the last block to render
  // would be the only one whose items could be shift-range selected.
  useOrderedGallerySelectedItems(selectableSections);

  /*
   * Nothing to draw: every block in the stack contributes nothing to the page. Keyed on
   * what RENDERS rather than on whether the page was authored — an empty showcase looks the same to
   * a viewer whether its default body was synthesized or written out, and both used to leave a
   * blank page once the flat gallery (which owned this null state) stopped being reachable.
   *
   * A block contributes nothing when it is hidden (a viewer never sees it) or when it is a listing
   * block whose category has no items. Anything else — text, title, image, video, links — is a body the
   * author wrote, and one of them is enough to keep the page from being empty.
   *
   * Hiding every block therefore DOES produce the empty state, and that is the principle rather
   * than an oversight: the viewer's page is blank either way, and a blank page with no explanation
   * is the thing this gate exists to prevent.
   *
   * `rendersEmptyBlocks` is the same point at the surface level: on the editor canvas every block
   * occupies space whether or not it has content, so there is always something on the page and the
   * author should be looking at their blocks, not at a viewer's empty state.
   *
   * `{ blocks: [] }` is excluded by the length check: a deliberately-empty page is an authored
   * choice, and it renders as the author left it.
   */
  const isStackEmpty =
    !rendersEmptyBlocks &&
    !itemsData.isLoading &&
    blocks.length > 0 &&
    blocks.every(
      (block) =>
        block.hidden ||
        (isListingContentBlock(block) && getPublicShowcaseListingSection(itemsData.data, block.type).total <= 0),
    );

  return (
    <div data-testid="PUBLIC_COLLECTION_BLOCKS">
      {showFlattenedChip ? <GalleryNestedEmptyStateHeader /> : null}
      {isStackEmpty ? (
        <PublicShowcaseNullState />
      ) : (
        blocks.map((block) => (
          <Fragment key={block.id}>
            {renderBlock({ block, children: getPublicShowcaseBlockContent(block, nestedChipHostCategory) })}
          </Fragment>
        ))
      )}
    </div>
  );
});

PublicShowcaseBlocks.displayName = 'PublicShowcaseBlocks';
